import { useState, useEffect, useRef, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface FormModalField {
  name: string;
  label: string;
  type?: 'text' | 'email' | 'textarea' | 'number' | 'select' | 'checkbox';
  placeholder?: string;
  required?: boolean;
  defaultValue?: any;
  options?: { label: string; value: any }[];
}

export interface FormModalProps {
  open: boolean;
  title?: string;
  fields: FormModalField[];
  confirmText?: string;
  cancelText?: string;
  theme?: string;
  wide?: boolean;
  onConfirm: (values: Record<string, any>) => void;
  onCancel: () => void;
}

export function FormModal({
  open,
  title = 'Enter Details',
  fields = [],
  confirmText = 'Submit',
  cancelText = 'Cancel',
  theme: themeProp,
  wide = false,
  onConfirm,
  onCancel,
}: FormModalProps) {
  const { theme: globalTheme } = useTheme();
  const theme = themeProp ?? globalTheme;
  const dialogRef = useRef<HTMLDivElement>(null);

  // Initialize form values from defaults
  const [values, setValues] = useState<Record<string, any>>(() => {
    const init: Record<string, any> = {};
    for (const field of fields) {
      init[field.name] = field.defaultValue ?? (field.type === 'checkbox' ? false : '');
    }
    return init;
  });

  // Reset when fields change
  useEffect(() => {
    const init: Record<string, any> = {};
    for (const field of fields) {
      init[field.name] = field.defaultValue ?? (field.type === 'checkbox' ? false : '');
    }
    setValues(init);
  }, [fields]);

  // Scroll lock + autofocus first input
  useEffect(() => {
    if (open) {
      document.body.classList.add('kstc-scroll-lock');
      // Focus the first input/textarea/select after render
      requestAnimationFrame(() => {
        const el = dialogRef.current?.querySelector<HTMLElement>(
          'input:not([type="checkbox"]):not([type="hidden"]), textarea, select'
        );
        el?.focus();
      });
    }
    return () => {
      document.body.classList.remove('kstc-scroll-lock');
    };
  }, [open]);

  // Escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    },
    [onCancel]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  const handleChange = (name: string, value: any) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(values);
  };

  if (!open) return null;

  return (
    <div data-kstc-modal className={`kstc-backdrop ${theme}`}>
      <div
        className={`kstc-dialog ${wide ? 'kstc-dialog--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        ref={dialogRef}
      >
        <div className="kstc-header">
          <h2 className="kstc-title">{title}</h2>
          <button
            className="kstc-x"
            onClick={onCancel}
            aria-label="Close"
            type="button"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="kstc-body">
            {fields.map((field) => (
              <div key={field.name} className="kstc-field">
                {field.type !== 'checkbox' && (
                  <label className="kstc-label" htmlFor={`kstc-field-${field.name}`}>
                    {field.label}
                    {field.required && <span className="kstc-required">*</span>}
                  </label>
                )}
                {renderField(field, values[field.name], handleChange)}
              </div>
            ))}
          </div>

          <div className="kstc-actions">
            <button
              className="kstc-btn kstc-btn--secondary"
              onClick={onCancel}
              type="button"
            >
              {cancelText}
            </button>
            <button className="kstc-btn kstc-btn--success" type="submit">
              {confirmText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function renderField(
  field: FormModalField,
  value: any,
  onChange: (name: string, value: any) => void
) {
  const id = `kstc-field-${field.name}`;

  switch (field.type) {
    case 'textarea':
      return (
        <textarea
          id={id}
          className="kstc-input"
          value={value}
          placeholder={field.placeholder}
          required={field.required}
          onChange={(e) => onChange(field.name, e.target.value)}
          rows={4}
        />
      );

    case 'number':
      return (
        <input
          id={id}
          className="kstc-input"
          type="number"
          value={value}
          placeholder={field.placeholder}
          required={field.required}
          onChange={(e) => onChange(field.name, e.target.valueAsNumber)}
        />
      );

    case 'select':
      return (
        <select
          id={id}
          className="kstc-input"
          value={value}
          required={field.required}
          onChange={(e) => onChange(field.name, e.target.value)}
        >
          <option value="">{field.placeholder || 'Select...'}</option>
          {field.options?.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );

    case 'checkbox':
      return (
        <label className="kstc-checkbox-label" htmlFor={id}>
          <input
            id={id}
            type="checkbox"
            checked={!!value}
            onChange={(e) => onChange(field.name, e.target.checked)}
          />
          {field.label}
        </label>
      );

    case 'email':
    default:
      return (
        <input
          id={id}
          className="kstc-input"
          type={field.type === 'email' ? 'email' : 'text'}
          value={value}
          placeholder={field.placeholder}
          required={field.required}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      );
  }
}
